import * as React from 'react';

import { enumSalesOrderStatus } from '@rematter/frontend-lib-api-client';
import { $resetLoadForm } from '@rematter/modules/trade/_state/loadForm.state';
import { Button, ConsoleDialog } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { LoadCreateForm } from './LoadCreateForm';

import {
  $createLoad,
  $loadCreateForm,
  $loadCreateLineItemsLoading,
  $nextLoadDocumentNumber,
  $resetLoadCreateForm,
} from '~/modules/trade/_state/loadCreateForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { tradeRoutePaths } from '~/modules/trade/routes';
import { transformLoadCreateInput } from '~/modules/trade/utils/loadCreate';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

interface LoadCreateProps {
  isOpen: boolean;
  requiredBookingId?: string;
  requiredSalesOrderId?: string;
  onClose: () => void;
  onSuccess?: (loadId: string) => void;
}

export const LoadCreate = ({
  isOpen,
  requiredBookingId,
  requiredSalesOrderId,
  onClose,
  onSuccess,
}: LoadCreateProps) => {
  const toast = useToast();
  const t = useTranslations();
  const [formData, updateForm] = useAtom($loadCreateForm);
  const isLoadingLineItems = useAtomValue($loadCreateLineItemsLoading);
  const resetForm = useSetAtom($resetLoadCreateForm);
  const resetLoadForm = useSetAtom($resetLoadForm);

  const [isCreatingLoad, setIsCreatingLoad] = React.useState(false);
  const [isLoadingDocNumber, setIsLoadingDocNumber] = React.useState(false);

  const createLoadMutation = useSetAtom($createLoad);
  const nextDocumentNumberMutation = useSetAtom($nextLoadDocumentNumber);
  const [, navigate] = useModuleLocation();

  const needsDocumentNumber = isOpen && !formData.loadNumber;

  React.useEffect(() => {
    if (!needsDocumentNumber) return;

    const abortController = new AbortController();
    setIsLoadingDocNumber(true);

    nextDocumentNumberMutation({})
      .then((data) => {
        if (abortController.signal.aborted) return;
        updateForm((f) => ({
          ...f,
          loadNumber: data.tradeLoadNextDocumentNumber,
        }));
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        console.error('Failed to get next document number:', error);
        onClose();
      })
      .finally(() => {
        if (abortController.signal.aborted) return;
        setIsLoadingDocNumber(false);
      });

    return () => {
      abortController.abort();
    };
  }, [needsDocumentNumber, nextDocumentNumberMutation, updateForm, onClose]);

  const handleClose = React.useCallback(() => {
    onClose();
    resetForm();
  }, [onClose, resetForm]);

  const validateCreateLoad = React.useCallback(() => {
    const errors: string[] = [];

    if (!formData.facilityId) {
      errors.push(t.loadsView.messages.facilityLocationRequired);
    }

    if (!formData.expectedShipDate) {
      errors.push(t.loadsView.messages.expectedShipDateRequired);
    }

    if (
      formData.salesOrderId &&
      formData.salesOrderStatus === enumSalesOrderStatus.Draft
    ) {
      errors.push(t.loadsView.messages.salesOrderDraftNotValid);
    }

    if (formData.salesOrderId && formData.lineItems.length === 0) {
      errors.push(t.loadsView.messages.lineItemsRequired);
    }

    return errors;
  }, [formData, t]);

  const handleSuccess = React.useCallback(
    (loadId: string) => {
      resetLoadForm();
      if (onSuccess) {
        onSuccess(loadId);
        return;
      }
      navigate(tradeRoutePaths.loadDetail, {
        params: { id: loadId },
        query: { tab: 'materials' },
        replace: true,
      });
    },
    [navigate, onSuccess, resetLoadForm],
  );

  const handleSubmit = React.useCallback(async () => {
    const errors = validateCreateLoad();
    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error));
      return;
    }

    try {
      setIsCreatingLoad(true);
      const result = await createLoadMutation(
        transformLoadCreateInput(formData),
      );

      toast.success(t.loadsView.messages.createSuccess);
      handleSuccess(result.tradeLoadCreate.id);
      handleClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.loadsView.messages.error,
      );
    } finally {
      setIsCreatingLoad(false);
    }
  }, [
    formData,
    createLoadMutation,
    toast,
    t,
    validateCreateLoad,
    handleSuccess,
    handleClose,
  ]);

  return (
    <ConsoleDialog
      className="@LoadCreate"
      isOpen={isOpen}
      onOpenChange={handleClose}
      title={t.loadsView.wizard.title}
      content={
        <LoadCreateForm
          requiredBookingId={requiredBookingId}
          requiredSalesOrderId={requiredSalesOrderId}
        />
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            color="primary"
            variant="solid"
            onPress={handleSubmit}
            isPending={
              isCreatingLoad || isLoadingLineItems || isLoadingDocNumber
            }
            isLoading={
              isCreatingLoad || isLoadingLineItems || isLoadingDocNumber
            }
            isDisabled={isLoadingLineItems || isLoadingDocNumber}
          >
            {formData.salesOrderId
              ? t.loadsView.wizard.buttons.saveAsPending
              : t.loadsView.wizard.buttons.saveAsUnassigned}
          </Button>
        </div>
      }
    />
  );
};
